import { ReportType } from '../interfaces/reporteria.interface'

/** Un reporte que de verdad existe, y dónde se genera. */
export interface ReportCatalogEntry {
  type: ReportType
  label: string
  icon: string
  /** La pregunta que contesta. Una línea, en el idioma de quien lo va a pedir. */
  answers: string
  /** Cómo se llama el lugar donde se genera, tal como aparece en el menú. */
  where: string
  route: string
  /** El permiso que abre esa pantalla. Sin él, el enlace se apaga en vez de llevar a un 403. */
  perm: [string, string]
}

/**
 * El catálogo de reportes de la plataforma.
 *
 * ## Por qué vive en el front y no en el backend
 *
 * Porque no es una lista de endpoints: es un mapa de **dónde se genera cada reporte**, y casi todos
 * se generan desde la pantalla donde viven sus datos. La API solo sabe registrar lo que ya se
 * generó; el camino de ida —a qué pestaña de Cobranza hay que ir— es conocimiento de la navegación.
 *
 * ## Por qué la lista NO trae un botón de «Generar» aquí
 *
 * Un reporte sin su pantalla es un reporte sin sus filtros. La cartera se saca del mes que estás
 * mirando, la mora de la fecha de corte que elegiste y la hoja de carga del archivo que acabas de
 * subir. Duplicar esos controles aquí sería mantener dos veces la misma decisión, y la copia se
 * desfasaría de la original en el primer parámetro nuevo.
 *
 *! Esta pantalla tenía siete opciones en un desplegable de las que **seis no existían**: `ACCE`,
 *! `BALAN`, `COLL`, `CUST`, `FLAG` y `NEW` devolvían «Tipo de reporte no válido» y la única que
 *! funcionaba era un volcado de tabla que nadie usaba. Lo que entra a esta lista se comprueba contra
 *! un endpoint real antes de escribirse.
 */
export const REPORT_CATALOG: ReportCatalogEntry[] = [
  {
    type: 'cartera',
    label: 'Cartera del mes',
    icon: 'fa-solid fa-wallet',
    answers: 'Qué se debía al empezar el mes, qué entró, qué se facturó de nuevo y qué quedó debiendo al cerrar.',
    where: 'Cobranza · Cartera',
    route: '/cobranza/cartera',
    perm: ['wallet', 'get-all'],
  },
  {
    type: 'avance',
    label: 'Avance de cobranza',
    icon: 'fa-solid fa-chart-line',
    answers: 'Cuánto del mes ya se cobró, con sus dos denominadores separados y los doce meses anteriores para comparar.',
    where: 'Cobranza · Avance',
    route: '/cobranza/avance',
    perm: ['payments', 'view'],
  },
  {
    type: 'central',
    label: 'CENTRAL',
    icon: 'fa-solid fa-table-list',
    answers: 'El libro de facturas del mes, renglón por renglón, con el desglose por concepto y lo que falta por cobrar de cada una.',
    where: 'Cobranza · CENTRAL',
    route: '/cobranza/central',
    perm: ['invoices', 'get-all'],
  },
  {
    type: 'cuadre-rentas',
    label: 'Cuadre de rentas',
    icon: 'fa-solid fa-scale-balanced',
    answers: 'Dónde lo facturado no coincide con la letra del contrato, y por cuánto.',
    where: 'Cobranza · CENTRAL',
    route: '/cobranza/central',
    perm: ['invoices', 'get-all'],
  },
  {
    type: 'mora',
    label: 'Clientes en mora',
    icon: 'fa-solid fa-triangle-exclamation',
    answers: 'Quién está atrasado, desde cuándo, y cuánto interés moratorio lleva acumulado a la fecha de corte.',
    where: 'Cobranza · Cartera',
    route: '/cobranza/cartera',
    perm: ['wallet', 'get-all'],
  },
  {
    type: 'conciliacion-bancaria',
    label: 'Hoja de carga de pagos',
    icon: 'fa-solid fa-building-columns',
    answers: 'Los depósitos del estado de cuenta ya identificados, en el layout exacto que lee el alta de pagos.',
    where: 'Cobranza · Conciliación',
    route: '/cobranza/conciliacion',
    perm: ['payments', 'reconcile'],
  },
  {
    type: 'corte-cartera',
    label: 'Corte de cartera',
    icon: 'fa-solid fa-file-csv',
    answers: 'El corte que se envía por correo a Contabilidad, a mano o en el día programado.',
    where: 'Configuración de Cartera · Cortes',
    route: '/wallet-config',
    perm: ['wallet-config', 'view'],
  },
]

/** El catálogo indexado por tipo, para unir con lo que devuelve el resumen. */
export const REPORT_CATALOG_BY_TYPE = new Map<ReportType, ReportCatalogEntry>(REPORT_CATALOG.map((r) => [r.type, r]))
